import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fadeUp, viewport } from '../utils/animations';
import Footer from '../components/Footer';

const { FiPlus, FiMinus, FiX, FiChevronLeft, FiChevronRight, FiArrowUpRight } = FiIcons;

const HERO_VIDEO_URL =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/823fd46511ee80485faa07ba27cddb78.mov';

const BTS_PHOTO_URL =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/6e71c43f2ceb94409efab64c1b27648b.png';

const BTS_VIDEO_URL =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/a5e87214f23febc2c9fc1d4a1d8ffdf0.mov';

const ROY_LOOP_URL =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/ba98355d036ae973b96193b46218dd36.mov';

const ALEN_LOOP_URL =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/5e776ebd263f1db5b39716bfe24752b4.mov';

const SAMPLE_CLIP_URL =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/25184deffb95d8cb4757f46bc5b7afb7.mp4';

const PAGE_CANONICAL = 'https://bayareapodcast.studio/mobile-podcast';
const PAGE_OG_IMAGE =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/0971b84d6b53811d44c91d144994fe4a.png';

// ─── SEO HELPERS ──────────────────────────────────────────────────────────────

function setMeta(selector, attr, value) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('=');
    el.setAttribute(attrName, attrVal.replace(/"/g, ''));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
  return el;
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (created) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, prev, created };
}

// ─── INTERSECTION OBSERVER HOOK ───────────────────────────────────────────────

function useInView(ref, threshold = 0.1) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

// ─── DATA ─────────────────────────────────────────────────────────────────────

const GALLERY_SLIDES = [
  { type: 'image', src: BTS_PHOTO_URL, alt: 'Mobile podcast production setup' },
  {
    type: 'image',
    src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/1091e6c7cf76c7b9abb2ced53d3b6e7d.png',
    alt: 'Mobile podcast shoot',
  },
  {
    type: 'image',
    src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/3aa237856106a27e4f86789f1e645314.png',
    alt: 'Mobile podcast shoot',
  },
  { type: 'video', src: BTS_VIDEO_URL, alt: 'Mobile podcast shoot — customer loop' },
  { type: 'video', src: ROY_LOOP_URL, alt: 'Mobile podcast shoot — loop reel' },
  { type: 'video', src: ALEN_LOOP_URL, alt: 'Mobile podcast shoot — on-location reel' },
];

const ROW_H = 72;

const BRAND_LOGOS = [
  { name: 'VMG Studios', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/48d2374d2a76e333e9ad42ce462e1723.webp', filter: 'brightness(0)', w: 180 },
  { name: 'At Will Media', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/5f46108f1a39b718ced46673e79b5200.webp', filter: 'brightness(0)', w: 220 },
  { name: 'Souvla', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/1c75fba2781610af504f957a63b15831.png', filter: 'grayscale(1) contrast(1.2)', w: 170 },
  { name: 'Brand 4', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/4c84cd14f3054baa811baba96b1e914c.png', filter: 'brightness(0)', w: 180 },
  { name: 'Brand 5', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/b07944bacf991e47510d6250b1381ce7.webp', filter: 'brightness(0)', w: 180 },
  { name: 'WRMSDC', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/f351a7f1a8161c136bc00c0e975d96b0.png', filter: 'grayscale(1) contrast(1.3)', w: 190 },
  { name: 'ROY', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/afd477d6c1eab50055742f1b00dcc764.png', filter: 'grayscale(1) brightness(0.2) contrast(2.2)', w: 160 },
  { name: 'Maximum Fun', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/8a80e536239acfbadd1c1bfc909e704a.png', filter: 'grayscale(1) contrast(1.5) brightness(0.8)', w: 230 },
  { name: 'Judge John Hodgman', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/42c8a71928ad43079fccb3bbcb9cf922.png', filter: 'grayscale(1) contrast(1.4)', w: 230 },
  { name: 'Une Femme', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/21603f8bf4733c535e48670320e467ac.png', filter: 'brightness(0)', w: 180 },
  { name: 'The One Recipe', url: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/1fb5ce53b0a8e88016891685c3d2b776.png', filter: 'grayscale(1) brightness(0.55) contrast(1.4)', w: 200 },
];

const SETUP_ITEMS = [
  { title: '(3) 4K CAMERAS', body: 'Multi-camera recording so your show looks clean in 4K, not like a Zoom call.' },
  { title: '(4) STUDIO MICROPHONES', body: 'Choice of traditional podcast mics or wireless clip-ons for up to 4 people.' },
  { title: 'STUDIO LIGHTING', body: 'We bring our studio lights on set so everyone on camera looks clear.' },
  { title: 'DEDICATED ON-SITE OPERATOR', body: 'We bring and handle all gear. We set up, monitor the recording, and pack everything away. You do not touch a single cable.' },
];

const PRICING = [
  {
    tier: '[ TIER 01 ]',
    title: 'Compact Mobile Shoot',
    price: '$800',
    features: [
      'Recording Time: Up to 1 hour',
      'Cameras: 2 separate 4K camera angles',
      'Microphones: Up to 2 people using studio microphones or wireless clip ons',
      'Lighting: Compact professional lighting setup adjusted to your room',
      'On Site Operator: 1 dedicated Operator to set up, monitor audio and video, and manage the recording',
      'Raw Deliverables: Separate unedited 4K camera files and separate audio tracks',
      'Cloud Delivery: Included',
    ],
    onSiteNote: 'Every session includes up to 1 hour before the scheduled recording time for setup and up to 1 hour after recording for load out. A 1 hour recording session means we may be on site for up to 3 hours total.',
  },
  {
    tier: '[ TIER 02 ]',
    title: 'Essential Mobile Shoot',
    price: '$1,200',
    features: [
      'Recording Time: Up to 2 hours',
      'Cameras: 3 separate 4K camera angles',
      'Microphones: Up to 4 people using studio microphones or wireless clip ons',
      'Lighting: Full mobile studio lighting setup adjusted to your room',
      'On Site Operator: 1 dedicated Operator to set up, monitor audio and video, and manage the recording',
      'Raw Deliverables: Separate unedited 4K camera files and separate multi track audio files',
      'Cloud Delivery: Included',
    ],
    onSiteNote: 'Every session includes up to 1 hour before the scheduled recording time for setup and up to 1 hour after recording for load out. A 2 hour recording session means we may be on site for up to 4 hours total.',
  },
  {
    tier: '[ TIER 03 ]',
    title: 'Extended Mobile Shoot',
    price: '$2,200',
    features: [
      'Recording Time: Up to 4 hours',
      'Cameras: 3 separate 4K camera angles',
      'Microphones: Up to 4 people using studio microphones or wireless clip ons',
      'Lighting: Full mobile studio lighting setup adjusted to your room',
      'On Site Operator: 1 dedicated Operator to set up, monitor audio and video, and manage the recording',
      'Raw Deliverables: Separate unedited 4K camera files and separate multi track audio files',
      'Cloud Delivery: Included',
    ],
    onSiteNote: 'Every session includes up to 1 hour before the scheduled recording time for setup and up to 1 hour after recording for load out. A 4 hour recording session means we may be on site for up to 6 hours total.',
  },
];

const POST_CARDS = [
  {
    title: 'FULL EPISODE EDIT',
    price: '$299 per episode',
    body: 'Complete multi-cam sync and switching, color correction, and audio mastering for one full episode. Delivered polished and ready to publish.',
  },
  {
    title: 'SOCIAL MEDIA CLIPS',
    price: '$199 per episode',
    body: 'A batch of vertical (9:16) clips pulled directly from a single edited episode. Fully captioned and optimized for Shorts, Reels, and TikTok.',
    note: '*Requires a Full Episode Edit',
  },
];

const PROOF_ITEMS = [
  {
    src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/2a4cce362f4ce74d1571ed933657c26f.webp',
    alt: 'Alien Earth — The Official Podcast',
    href: 'https://podcasts.apple.com/us/podcast/alien-earth-the-official-podcast/id1824557490',
  },
  {
    src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/e756aebe08ce2568a550a59f66f3e705.webp',
    alt: 'Jen Pelka — CEO, Une Femme Wines',
    href: 'https://www.thisisroy.com/podcast-episodes/hot-gossip-a-conversation-with-jen-pelka',
  },
  {
    src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/09a96018e8e80c73f4dbefe0bab76485.webp',
    alt: 'Charles Bililies — Founder & CEO, Souvla',
    href: 'https://www.thisisroy.com/podcast-episodes/hot-gossip-a-conversation-with-charles-billies',
  },
  {
    src: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/1fb5ce53b0a8e88016891685c3d2b776.png',
    alt: 'The One Recipe with Jesse Sparks',
    href: 'https://youtu.be/1UjXw6-Pig0?si=SVCgfoeIBuLK3zIE',
  },
];

const STEPS = [
  {
    num: '01 //',
    title: 'DISCOVERY CALL',
    body: 'We hop on a quick call to verify the details, confirm the location, and go over exactly what you need for the session.',
  },
  {
    num: '02 //',
    title: 'DEPOSIT & LOCK',
    body: 'A 50% deposit officially locks down your shoot date and time on the calendar.',
  },
  {
    num: '03 //',
    title: 'SHOOT DAY',
    body: 'We show up 1 hour early to set up the equipment and hit record. After the session wraps, we require 1 hour on-site to break down and pack up.',
  },
  {
    num: '04 //',
    title: 'DELIVERY',
    body: 'Raw files or fully edited episodes hit your inbox via a secure cloud link. Turnaround is typically 7 to 10 business days, depending on the final scope of the project.',
  },
];

const FAQS = [
  {
    q: 'What areas do you travel to?',
    a: 'We provide Mobile Podcast Shoots throughout the Bay Area and surrounding regions. Your exact recording location is reviewed before the final quote is issued. Additional travel charges may apply depending on distance, travel time, tolls, parking requirements, or unusual transportation logistics. Any additional travel cost will be confirmed before your deposit is paid.',
  },
  {
    q: 'What do I need to provide?',
    a: 'Location details, power access, seating, and parking reimbursement. We walk through all layout specifics on your discovery call.',
  },
  {
    q: 'How do you handle room noise or echoes?',
    a: 'On-site recording captures your room as-is. Background noise and audio leveling are only cleaned up during post-production if you order an Episode Edit. Raw files remain completely unedited.',
  },
  {
    q: 'How many people can be on camera?',
    a: 'Up to 4 people maximum.',
  },
  {
    q: 'When do I get my files?',
    a: 'Unedited raw files are delivered via a secure cloud link within 7 to 10 business days. Note: Because 4K video files are massive, all cloud download links expire and files are permanently cleared from our cloud servers after 30 days.',
  },
  {
    q: 'What is your cancellation and reschedule policy?',
    a: 'The 50% deposit is non-refundable. You can reschedule once for free with at least 48 hours notice.',
  },
];

const TIDYCAL_URL = 'https://mobile.bayareapodcast.studio/discovery';

// ─── SUBCOMPONENTS ────────────────────────────────────────────────────────────

function ProofCard({ item }) {
  const [hovered, setHovered] = useState(false);

  const inner = (
    <div
      className="relative w-full overflow-hidden cursor-pointer"
      style={{ aspectRatio: '1 / 1' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={item.src}
        alt={item.alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          transform: hovered ? 'scale(1.06)' : 'scale(1)',
          transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: 'rgba(0,0,0,0.45)',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.35s ease',
        }}
      >
        <div
          className="w-12 h-12 border border-white flex items-center justify-center"
          style={{
            transform: hovered ? 'scale(1)' : 'scale(0.8)',
            transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <SafeIcon icon={FiArrowUpRight} className="text-white text-xl" />
        </div>
      </div>
    </div>
  );

  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.alt} className="block">
        {inner}
      </a>
    );
  }
  return <div>{inner}</div>;
}

function GallerySlide({ asset, active }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (active) {
      if (el.readyState === 0) el.load();
      el.currentTime = 0;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [active]);

  if (asset.type === 'video') {
    return (
      <video
        ref={videoRef}
        src={asset.src}
        muted loop playsInline preload="none"
        aria-label={asset.alt}
        className="w-full h-full object-cover absolute inset-0"
      />
    );
  }
  return (
    <img
      src={asset.src}
      alt={asset.alt}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover absolute inset-0"
    />
  );
}

function GalleryModal({ onClose }) {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const touchStartX = useRef(null);
  const total = GALLERY_SLIDES.length;

  const go = useCallback((next, direction) => {
    setDir(direction);
    setIndex(next);
  }, []);

  const prev = useCallback(() => go((index - 1 + total) % total, -1), [index, total, go]);
  const next = useCallback(() => go((index + 1) % total, 1), [index, total, go]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, prev, next]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
    touchStartX.current = null;
  };

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  const pad = (n) => String(n + 1).padStart(2, '0');

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4 md:p-[5vw]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          className="relative w-full max-w-5xl bg-[#0a0a0a] flex flex-col overflow-hidden border border-white/10"
          style={{ maxHeight: '90vh' }}
          initial={{ opacity: 0, scale: 0.97, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 12 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 border-b border-white/10">
            <div className="flex items-center justify-between px-4 md:px-7 py-3.5">
              <h2 className="text-xs md:text-sm font-black text-white tracking-[0.15em] uppercase leading-none">
                MOBILE PODCAST SHOOT
              </h2>
              <div className="flex items-center gap-4">
                {total > 1 && (
                  <span className="text-[11px] font-mono font-bold text-zinc-500 tracking-widest tabular-nums">
                    {pad(index)} / {pad(total - 1)}
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center border border-white/15 text-white hover:bg-white/10 transition-colors shrink-0"
                  aria-label="Close gallery"
                >
                  <SafeIcon icon={FiX} className="text-base" />
                </button>
              </div>
            </div>
          </div>
          <div
            className="relative w-full shrink-0 select-none overflow-hidden"
            style={{ aspectRatio: '16 / 9' }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.div
                key={index}
                custom={dir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <GallerySlide asset={GALLERY_SLIDES[index]} active={true} />
              </motion.div>
            </AnimatePresence>
            {total > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center border border-white/20 bg-black/60 text-white hover:bg-black/90 transition-colors"
                  aria-label="Previous slide"
                >
                  <SafeIcon icon={FiChevronLeft} className="text-lg md:text-xl" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center border border-white/20 bg-black/60 text-white hover:bg-black/90 transition-colors"
                  aria-label="Next slide"
                >
                  <SafeIcon icon={FiChevronRight} className="text-lg md:text-xl" />
                </button>
                <div className="absolute bottom-3.5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                  {GALLERY_SLIDES.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => go(i, i > index ? 1 : -1)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${
                        i === index ? 'bg-white scale-125' : 'bg-white/30 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function LogoStrip() {
  const LogoSet = () => (
    <div className="flex items-center gap-12 md:gap-16 shrink-0 pr-12 md:pr-16">
      {BRAND_LOGOS.map((logo, i) => (
        <div
          key={`${logo.name}-${i}`}
          className="flex-none flex items-center justify-center"
          style={{ width: `${logo.w}px`, height: `${ROW_H}px` }}
        >
          <img
            src={logo.url}
            alt={`${logo.name} logo`}
            loading="lazy"
            decoding="async"
            style={{
              maxWidth: `${logo.w}px`,
              maxHeight: `${ROW_H}px`,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              filter: logo.filter,
              opacity: 0.82,
              transition: 'opacity 0.3s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.82'; }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <section className="bg-white py-12 md:py-14 border-b border-neutral-200">
      <style>{`
        @keyframes mobile-marquee {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .mobile-marquee-track {
          display: inline-flex;
          animation: mobile-marquee 55s linear infinite;
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .mobile-marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>
      <p className="text-xs font-bold tracking-widest uppercase text-black text-center mb-8 md:mb-10 px-6">
        BRANDS CAPTURED ON CAMERA
      </p>
      <div className="relative overflow-hidden w-full before:absolute before:left-0 before:top-0 before:bottom-0 before:w-16 md:before:w-24 before:z-10 before:bg-gradient-to-r before:from-white before:to-transparent after:absolute after:right-0 after:top-0 after:bottom-0 after:w-16 md:after:w-24 after:z-10 after:bg-gradient-to-l after:from-white after:to-transparent">
        <div className="mobile-marquee-track">
          <LogoSet />
          <LogoSet />
        </div>
      </div>
    </section>
  );
}

function HeroVideo() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onCanPlay = () => {
      setReady(true);
      el.play().catch(() => {});
    };
    el.addEventListener('canplaythrough', onCanPlay);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { el.play().catch(() => {}); }
        else { el.pause(); }
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      el.removeEventListener('canplaythrough', onCanPlay);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full min-h-[240px] sm:min-h-[320px] lg:min-h-[480px] border border-neutral-800 bg-black overflow-hidden"
    >
      <video
        ref={videoRef}
        src={HERO_VIDEO_URL}
        muted loop playsInline preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease' }}
      />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-950">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        </div>
      )}
      <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 z-10">
        <div className="bg-black/80 backdrop-blur-md px-2.5 md:px-3 py-1.5 md:py-2">
          <p className="font-mono text-[9px] md:text-[10px] font-bold tracking-[0.18em] text-white uppercase leading-none">
            JEN PELKA — FOUNDER, UNE FEMME WINES
          </p>
        </div>
      </div>
    </div>
  );
}

function BTSMedia({ onOpenGallery }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const inView = useInView(containerRef, 0.2);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (hovered && inView) {
      if (el.readyState === 0) el.load();
      el.currentTime = 0;
      el.play().catch(() => {});
    } else {
      el.pause();
      if (!hovered) el.currentTime = 0;
    }
  }, [hovered, inView]);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden border-2 border-black cursor-pointer group"
      onClick={onOpenGallery}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      aria-label="View setup gallery"
      onKeyDown={(e) => e.key === 'Enter' && onOpenGallery()}
    >
      <img
        src={BTS_PHOTO_URL}
        alt="Mobile podcast production setup"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
        style={{ opacity: hovered ? 0 : 1 }}
        loading="lazy"
        decoding="async"
      />
      <video
        ref={videoRef}
        src={BTS_VIDEO_URL}
        muted loop playsInline preload="none"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
        style={{ opacity: hovered ? 1 : 0 }}
      />
    </div>
  );
}

function SampleClipPlayer() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const inView = useInView(containerRef, 0.15);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (inView) {
      const onCanPlay = () => {
        setReady(true);
        el.play().catch(() => {});
      };
      el.addEventListener('canplaythrough', onCanPlay);
      if (el.readyState === 0) el.load();
      else { setReady(true); el.play().catch(() => {}); }
      return () => el.removeEventListener('canplaythrough', onCanPlay);
    } else {
      el.pause();
    }
  }, [inView]);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      style={{ aspectRatio: '9 / 16', maxHeight: '580px' }}
      className="relative w-full mx-auto"
    >
      <div
        ref={containerRef}
        className="relative w-full h-full border-2 border-black bg-black overflow-hidden"
      >
        <video
          ref={videoRef}
          src={SAMPLE_CLIP_URL}
          muted loop playsInline preload="none"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.5s ease' }}
        />
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-950">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function FaqItem({ q, a, isFirst }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border-b border-white/20${isFirst ? ' border-t border-white/20' : ''}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full py-5 md:py-6 text-left flex justify-between items-start gap-4 focus:outline-none group"
        aria-expanded={open}
      >
        <span className="font-black text-base md:text-lg uppercase tracking-wide text-white leading-snug">{q}</span>
        <span className="shrink-0 mt-0.5 text-white group-hover:text-white transition-colors">
          <SafeIcon icon={open ? FiMinus : FiPlus} className="text-lg" />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="text-base md:text-lg text-white leading-relaxed pb-6 pr-6 md:pr-8">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FeedspaceWidget() {
  useEffect(() => {
    if (document.querySelector('script[src="https://js.feedspace.io/v1/embed/embed.min.js"]')) return;
    const script = document.createElement('script');
    script.src = 'https://js.feedspace.io/v1/embed/embed.min.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="feedspace-embed" data-id="66f7ce5c-aa53-4a83-8891-e0237c83fabf" />
  );
}

// ─── TIDYCAL EMBED ────────────────────────────────────────────────────────────
// Use a plain iframe instead of TidyCal's script-based embed.
// The iframe is self-contained — it loads TidyCal's page directly and
// never disappears on SPA back-navigation because it has no DOM dependency.

function TidyCalEmbed() {
  return (
    <iframe
      src="https://tidycal.com/bayareapodcaststudio/discovery"
      title="Schedule a Discovery Call"
      loading="lazy"
      style={{
        width: '100%',
        minHeight: '600px',
        border: 'none',
        display: 'block',
      }}
      allow="payment"
    />
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────

export default function MobilePodcast() {
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    const PAGE_TITLE = 'Mobile Podcast Recording & Production | Bay Area Podcast Studio';
    const PAGE_DESC  = 'Broadcast-quality mobile podcast studio delivered to your Bay Area office or venue. Multi-cam 4K video, pro audio, and an on-site operator. Book a call today.';
    const OG_TITLE   = 'On-Location Mobile Podcast Studio | Bay Area Podcast Studio';
    const OG_DESC    = 'We bring a full 4K multi-camera studio, lighting, and dedicated operator directly to your office or venue anywhere in the Bay Area.';

    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const descEl   = setMeta('meta[name="description"]',         'content', PAGE_DESC);
    const prevDesc = descEl.getAttribute('content');

    const { el: canonEl, prev: prevCanon } = setLink('canonical', PAGE_CANONICAL);

    const ogTitleEl = setMeta('meta[property="og:title"]',       'content', OG_TITLE);
    const ogDescEl  = setMeta('meta[property="og:description"]', 'content', OG_DESC);
    const ogUrlEl   = setMeta('meta[property="og:url"]',         'content', PAGE_CANONICAL);
    const ogImgEl   = setMeta('meta[property="og:image"]',       'content', PAGE_OG_IMAGE);
    const prevOgT   = ogTitleEl.getAttribute('content');
    const prevOgD   = ogDescEl.getAttribute('content');
    const prevOgU   = ogUrlEl.getAttribute('content');
    const prevOgI   = ogImgEl.getAttribute('content');

    const twTitleEl = setMeta('meta[name="twitter:title"]',       'content', OG_TITLE);
    const twDescEl  = setMeta('meta[name="twitter:description"]', 'content', OG_DESC);
    const twImgEl   = setMeta('meta[name="twitter:image"]',       'content', PAGE_OG_IMAGE);
    const prevTwT   = twTitleEl.getAttribute('content');
    const prevTwD   = twDescEl.getAttribute('content');
    const prevTwI   = twImgEl.getAttribute('content');

    return () => {
      document.title = prevTitle;
      descEl.setAttribute('content', prevDesc);
      canonEl.setAttribute('href', prevCanon);
      ogTitleEl.setAttribute('content', prevOgT);
      ogDescEl.setAttribute('content', prevOgD);
      ogUrlEl.setAttribute('content', prevOgU);
      ogImgEl.setAttribute('content', prevOgI);
      twTitleEl.setAttribute('content', prevTwT);
      twDescEl.setAttribute('content', prevTwD);
      twImgEl.setAttribute('content', prevTwI);
    };
  }, []);

  const scrollToCall = (e) => {
    e.preventDefault();
    const el = document.getElementById('discovery-call');
    if (el) {
      const offset = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-black selection:text-white">
      {galleryOpen && <GalleryModal onClose={() => setGalleryOpen(false)} />}

      <header className="fixed top-0 left-0 right-0 z-50 h-[64px] md:h-[72px] bg-black/90 backdrop-blur border-b border-white/10 flex items-center justify-between px-5 md:px-12">
        <Link to="/" className="flex items-center group hover:opacity-75 transition-opacity">
          <span className="text-white font-black text-lg md:text-xl tracking-tighter">BA</span>
          <span className="h-4 w-[1px] bg-white/30 mx-2.5 md:mx-3 inline-block" />
          <span className="text-[10px] md:text-[11px] font-black tracking-[0.2em] text-white uppercase hidden sm:inline-block">
            BAY AREA PODCAST STUDIO
          </span>
        </Link>
        <a
          href="#discovery-call"
          onClick={scrollToCall}
          className="bg-white text-black font-black text-[10px] uppercase tracking-[0.15em] px-3 md:px-4 py-2.5 border border-white hover:bg-black hover:text-white transition-colors duration-150"
        >
          BOOK CALL
        </a>
      </header>

      <main className="pt-[64px] md:pt-[72px]">
        <section className="bg-black text-white px-5 md:px-12 py-14 md:py-24 lg:py-28">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
            <motion.div className="flex flex-col justify-center order-2 lg:order-1" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <p className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase mb-4 text-white">[ MOBILE PODCAST SHOOT ]</p>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[1.0] mb-5 md:mb-6">
                The Studio Standard.<br />Delivered to Your Location.
              </h1>
              <p className="text-base md:text-lg leading-relaxed max-w-xl mb-8 md:mb-10" style={{ color: '#E5E5E5' }}>
                We bring the podcast studio experience directly to your office, venue, or location. We supply the equipment and operator. You host your conversation, we handle the rest.
              </p>
              <a href="#discovery-call" onClick={scrollToCall} className="inline-block bg-white text-black font-black text-xs uppercase tracking-[0.18em] px-7 md:px-8 py-4 border-2 border-white hover:bg-black hover:text-white transition-colors duration-150 self-start">
                SCHEDULE A DISCOVERY CALL
              </a>
            </motion.div>
            <motion.div className="w-full order-1 lg:order-2" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <HeroVideo />
            </motion.div>
          </div>
        </section>

        <LogoStrip />

        <section className="bg-white text-black px-5 md:px-12 py-16 md:py-24 lg:py-28 border-b-2 border-black">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
            <motion.div className="flex flex-col gap-4" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <BTSMedia onOpenGallery={() => setGalleryOpen(true)} />
              <button type="button" onClick={() => setGalleryOpen(true)} className="w-full border-2 border-black text-black font-black text-xs uppercase tracking-[0.18em] py-4 hover:bg-black hover:text-white transition-colors duration-150 cursor-pointer">
                VIEW GALLERY
              </button>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <p className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase mb-4 text-black">[ ON-LOCATION SETUP ]</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-tight mb-8 md:mb-10 text-black">
                EVERYTHING WE BRING TO YOUR DOOR.
              </h2>
              <div className="flex flex-col">
                {SETUP_ITEMS.map((item, i) => (
                  <div key={item.title} className={`py-5 md:py-6 ${i < SETUP_ITEMS.length - 1 ? 'border-b border-black' : ''}`}>
                    <p className="font-black text-base md:text-lg lg:text-xl uppercase tracking-wide mb-2 text-black">{item.title}</p>
                    <p className="text-base md:text-lg text-black leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-black text-white px-5 md:px-12 py-16 md:py-24 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <motion.div className="mb-10 md:mb-14 lg:mb-16 text-center" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight mb-3 md:mb-4">
                MOBILE PODCAST SESSION RATES
              </h2>
              <p className="text-base md:text-lg text-white leading-relaxed">
                Simple, transparent pricing for professional mobile podcast recording at your location.
              </p>
            </motion.div>
            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 lg:gap-8" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              {PRICING.map((card) => (
                <div key={card.tier} className="border-2 border-white p-6 md:p-8 lg:p-10 flex flex-col">
                  <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-white mb-3 md:mb-4">{card.tier}</p>
                  <h3 className="font-black text-xl md:text-2xl uppercase tracking-tight mb-2 text-white">{card.title}</h3>
                  <p className="font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter mb-6 md:mb-8 text-white">{card.price}</p>
                  <ul className="flex flex-col gap-3 md:gap-4 mb-6 md:mb-8">
                    {card.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-base md:text-lg text-white leading-snug">
                        <span className="text-white mt-1 shrink-0">—</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto border-t border-white/25 pt-5 md:pt-6">
                    <p className="font-mono text-[10px] font-black tracking-[0.18em] text-white uppercase mb-2 md:mb-3">TOTAL ON-SITE PROCESS</p>
                    <p className="text-base md:text-lg text-white leading-relaxed">{card.onSiteNote}</p>
                  </div>
                </div>
              ))}
            </motion.div>
            <motion.div className="mt-10 md:mt-12 border-t border-white/20 pt-8 md:pt-10 text-center" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <p className="text-base md:text-lg font-bold text-white leading-relaxed max-w-3xl mx-auto">
                Raw audio and video files are delivered by secure cloud link within 7 to 10 business days. Clients may also arrange to provide a compatible SSD for direct file transfer when appropriate. Cloud files remain available for 30 days. Compact Mobile Shoots support up to 2 active participants. Essential and Extended Mobile Shoots support up to 4 active participants. Overtime is billed at $250 per additional 30 minutes.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="bg-white text-black px-5 md:px-12 py-16 md:py-24 lg:py-28 border-b-2 border-black">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <p className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase mb-4 text-black">[ OPTIONAL ADD-ONS ]</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-tight mb-8 md:mb-10">EDITING &amp; POST-PRODUCTION</h2>
              <div className="flex flex-col gap-5 md:gap-6">
                {POST_CARDS.map((card) => (
                  <motion.div key={card.title} className="border-2 border-black p-5 md:p-7 lg:p-8 cursor-default" whileHover={{ scale: 1.015, boxShadow: '4px 4px 0px 0px #000000' }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="font-black text-base md:text-lg lg:text-xl uppercase tracking-wide text-black">{card.title}</h3>
                      <span className="font-mono font-bold text-sm tracking-widest text-black shrink-0 mt-0.5 whitespace-nowrap">{card.price}</span>
                    </div>
                    <p className="text-base md:text-lg text-black leading-relaxed">{card.body}</p>
                    {card.note && <p className="mt-3 font-mono text-xs text-black tracking-wide">{card.note}</p>}
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div className="w-full flex items-start justify-center lg:justify-end" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <div className="w-full max-w-[260px] sm:max-w-xs"><SampleClipPlayer /></div>
            </motion.div>
          </div>
        </section>

        <section className="bg-black px-5 md:px-12 py-16 md:py-24 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <motion.div className="mb-8 md:mb-12 lg:mb-14 text-center" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">SEE US IN ACTION</h2>
            </motion.div>
            <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              {PROOF_ITEMS.map((item) => <ProofCard key={item.alt} item={item} />)}
            </motion.div>
          </div>
        </section>

        <section className="bg-neutral-950 text-white px-5 md:px-12 py-16 md:py-24 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <motion.div className="mb-10 md:mb-14 lg:mb-16" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">HOW IT WORKS</h2>
            </motion.div>
            <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              {STEPS.map((step, i) => (
                <div key={step.num} className={[
                  'py-7 md:py-8 px-0 sm:px-5 lg:px-8',
                  'border-b border-white/10',
                  i < STEPS.length - 1 ? 'sm:border-r sm:border-white/10' : '',
                  i === 0 ? 'sm:pl-0' : '',
                  i === STEPS.length - 1 ? 'border-b-0' : '',
                  'sm:last:border-b-0',
                ].join(' ')}>
                  <p className="font-mono text-xs font-bold tracking-[0.2em] text-white mb-3 md:mb-4 uppercase">{step.num}</p>
                  <h3 className="font-black text-lg md:text-xl lg:text-2xl uppercase tracking-tight mb-3 text-white">{step.title}</h3>
                  <p className="text-base md:text-lg text-white leading-relaxed">{step.body}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="bg-black text-white px-5 md:px-12 py-16 md:py-24 lg:py-28">
          <div className="max-w-4xl mx-auto">
            <motion.div className="mb-10 md:mb-12" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <p className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase mb-4 text-white">[ FREQUENTLY ASKED QUESTIONS ]</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">Mobile Podcast FAQ</h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              {FAQS.map((item, i) => <FaqItem key={i} q={item.q} a={item.a} isFirst={i === 0} />)}
            </motion.div>
          </div>
        </section>

        <section className="bg-neutral-950 px-5 md:px-12 py-16 md:py-24 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <motion.div className="mb-8 md:mb-12 lg:mb-14 text-center" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">CLIENT EXPERIENCES</h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}><FeedspaceWidget /></motion.div>
          </div>
        </section>

        <section id="discovery-call" className="bg-white text-black">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 min-h-[480px] lg:min-h-[520px]">
            <motion.div className="flex flex-col justify-center px-6 md:px-14 lg:px-16 py-14 md:py-20" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-tight mb-4">Ready to Record On-Location?</h2>
              <p className="text-base md:text-lg text-black leading-relaxed max-w-sm mb-8">
                Pick a 15-minute slot to review your venue, guest count, and gear requirements before locking in your session.
              </p>
              <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-black text-white font-black text-xs uppercase tracking-[0.18em] px-6 py-3.5 hover:bg-neutral-800 transition-colors duration-150 self-start">
                OPEN BOOKING PAGE
                <SafeIcon icon={FiArrowUpRight} className="text-sm" />
              </a>
            </motion.div>
            <motion.div className="flex items-stretch px-6 md:px-14 lg:px-10 py-14 md:py-20 bg-neutral-50 border-t lg:border-t-0 lg:border-l border-neutral-200" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
              <div className="w-full"><TidyCalEmbed /></div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}